import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Jobs, type Job } from '@/lib/api/v1';
import { Badge, Button, Card, EmptyState, Icon, Skeleton } from '@/design-system';
import { useToasts } from '@/lib/store';

const STATUS_FILTERS: { id: 'all' | Job['status']; label: string }[] = [
  { id: 'all', label: 'Alle' },
  { id: 'queued', label: 'Køet' },
  { id: 'running', label: 'Kjører' },
  { id: 'completed', label: 'Fullført' },
  { id: 'failed', label: 'Feilet' },
  { id: 'cancelled', label: 'Avbrutt' },
];

export function AdminJobsPage() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [filter, setFilter] = useState<'all' | Job['status']>('all');

  const jobsQ = useQuery({
    queryKey: ['admin.jobs', filter],
    queryFn: () => Jobs.list(filter === 'all' ? { limit: 100 } : { limit: 100, status: filter }),
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const run = useMutation({
    mutationFn: (id: string) => Jobs.run(id),
    onSuccess: () => {
      push({ title: 'Jobben startet på nytt', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['admin.jobs'] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke kjøre jobb', description: err.message, tone: 'danger' }),
  });

  const jobs = jobsQ.data?.jobs ?? [];

  return (
    <div className="grid gap-6 max-w-5xl">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="text-xs uppercase tracking-widest text-mist hover:text-candle">
            ← Tilbake til admin
          </Link>
          <h1 className="ui-section-title text-3xl">Jobber</h1>
          <p className="text-sm text-mist">Bakgrunns-jobber: katalogsynk, vinmonopol-skanninger, paring, anbefalinger.</p>
        </div>
        <Button variant="ghost" iconLeft="refresh" onClick={() => jobsQ.refetch()} loading={jobsQ.isFetching}>
          Oppdater
        </Button>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setFilter(s.id)}
            className={`ui-pill ${filter === s.id ? 'ui-pill--active' : ''}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <Card className="grid gap-3 p-5">
        {jobsQ.isLoading ? (
          <div className="grid gap-2"><Skeleton height={48} /><Skeleton height={48} /><Skeleton height={48} /></div>
        ) : jobs.length === 0 ? (
          <EmptyState icon="container" title="Ingen jobber" description="Ingen bakgrunnsjobber matcher filteret." />
        ) : (
          <ul className="grid gap-2">
            {jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                onRun={() => run.mutate(job.id)}
                running={run.isPending && run.variables === job.id}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function JobRow({ job, onRun, running }: { job: Job; onRun: () => void; running: boolean }) {
  const tone: 'gold' | 'mist' | 'ruby' =
    job.status === 'completed' || job.status === 'done'
      ? 'gold'
      : job.status === 'failed'
        ? 'ruby'
        : 'mist';
  const isTerminal = job.status === 'completed' || job.status === 'done' || job.status === 'failed' || job.status === 'cancelled';
  const created = new Date(job.createdAt).toLocaleString('nb-NO');
  return (
    <li className="grid gap-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-3">
        <Icon name="container" size={14} className="text-mist" />
        <span className="font-mono text-xs text-parchment">{job.type}</span>
        <Badge tone={tone}>{job.status}</Badge>
        {job.attempts != null && job.maxAttempts != null && (
          <span className="text-[10px] uppercase tracking-widest text-mist">
            forsøk {job.attempts}/{job.maxAttempts}
          </span>
        )}
        <span className="ml-auto text-[10px] uppercase tracking-widest text-mist">{created}</span>
        {isTerminal && (
          <Button size="sm" variant="ghost" iconLeft="refresh" loading={running} onClick={onRun}>
            Kjør på nytt
          </Button>
        )}
      </div>
      {job.error && (
        <pre className="overflow-x-auto rounded bg-ink-soft p-2 text-[11px] text-[color:var(--ui-color-danger)]">
          {job.error}
        </pre>
      )}
      <div className="text-[10px] uppercase tracking-widest text-mist">id {job.id}</div>
    </li>
  );
}
